import type { RequestBody } from '../../api/apiService';
import { APIService } from '../../api/apiService';
import type FileWithToken from '../../interfaces/FileWithToken';
import type { PublicFile, PublicFilePasswordRequired } from '../../interfaces/PublicFileApiService';

/** Options used by PublicFileService: no Bearer token (anonymous access). */
const NO_BEARER: { skipAuth: true } = { skipAuth: true };

/**
 * Service for anonymous access to publicly shared files (no Bearer token).
 * All requests set skipAuth: true so the client does not send the Authorization header.
 */
export class PublicFileService extends APIService {
  /**
   * Checks if the public link requires a password (anonymous).
   */
  checkPublicSharePassword(token: string): Promise<PublicFilePasswordRequired> {
    return this.request(
      `/public/${token}/check`,
      'GET',
      undefined,
      undefined,
      undefined,
      NO_BEARER,
    );
  }

  /**
   * Gets file/folder for a public link (anonymous).
   * Use accessCode when the link is password-protected.
   * When folderId is provided, returns the content of that folder (same response shape as root).
   */
  getPublicFile(
    token: string,
    offset: number,
    count: number,
    accessCode?: string,
    folderId?: number,
  ): Promise<PublicFile> {
    const qs = `offset=${offset}&count=${count}${folderId != null ? `&folderId=${folderId}` : ''}`;
    const url = `/public/${token}?${qs}`;
    if (accessCode) {
      return this.request(
        url,
        'POST',
        { accessCode } as RequestBody,
        undefined,
        undefined,
        NO_BEARER,
      );
    }
    return this.request(url, 'GET', undefined, undefined, undefined, NO_BEARER);
  }

  /**
   * Gets file/folder info for a public link (anonymous).
   * Use accessCode when the link is password-protected.
   * When folderId is provided, returns the content of that folder (same response shape as root).
   */
  getPublicFileForViewer(
    token: string,
    childFileId?: number,
    accessCode?: string,
  ): Promise<FileWithToken> {
    const qs = childFileId != null ? `childFileId=${childFileId}` : '';
    const url = qs ? `/public/${token}/viewer?${qs}` : `/public/${token}/viewer`;
    if (accessCode) {
      return this.request(
        url,
        'POST',
        { accessCode } as RequestBody,
        undefined,
        undefined,
        NO_BEARER,
      );
    }
    return this.request(url, 'GET', undefined, undefined, undefined, NO_BEARER);
  }

  /**
   * Unlocks a public file (anonymous).
   * Sends offset and count as required by the BE when sending the password.
   */
  unlockPublicFile(
    token: string,
    accessCode: string,
    offset: number,
    count: number,
    folderId?: number,
  ): Promise<PublicFile> {
    const qs = `offset=${offset}&count=${count}${folderId != null ? `&folderId=${folderId}` : ''}`;
    return this.request(
      `/public/${token}?${qs}`,
      'POST',
      { accessCode },
      undefined,
      undefined,
      NO_BEARER,
    );
  }

  /**
   * Downloads a file from a public link (anonymous).
   * For folder shares, pass childFileId to download a specific file inside.
   * Returns blob plus response headers (Content-Disposition, optional X-Content-SHA256).
   */
  async downloadPublicFile(
    token: string,
    childFileId?: number,
    accessCode?: string,
  ): Promise<{ blob: Blob; headers: Record<string, unknown> }> {
    const endpoint =
      childFileId != null
        ? `/public/${token}/download?childFileId=${childFileId}`
        : `/public/${token}/download`;
    const res = await this.requestRaw<Blob>(
      endpoint,
      accessCode ? 'POST' : 'GET',
      accessCode ? ({ accessCode } as RequestBody) : undefined,
      undefined,
      undefined,
      { responseType: 'blob', ...NO_BEARER },
    );
    return {
      blob: res.data as Blob,
      headers: (res.headers ?? {}) as Record<string, unknown>,
    };
  }
}
